import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, buildWhatsAppLink } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { BRAND } from "@/lib/brand";
import { fireEnhancedConversion } from "@/lib/gtag";

export default function ContactSection({ whatsappNumber }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t.contact.required); return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      // Google Ads Enhanced Conversions for Leads (plaintext — Google hashes server-side)
      fireEnhancedConversion({ email: form.email, phone: form.phone, name: form.name });
      const text = `Hola Aura, soc ${form.name} (${form.email} · ${form.phone}). ${form.message || ""}`;
      window.open(buildWhatsAppLink(whatsappNumber, text), "_blank");
      toast.success(t.contact.sent);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#2e5949] text-[#fdfcf9] aura-grain"
    >
      <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }}
        >
          <p className="uppercase tracking-[0.28em] text-xs text-[#E2C9A0] mb-4">{t.contact.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {t.contact.title1}
            <span className="font-script text-[#5daa92]"> {t.contact.titleHl} </span>
            {t.contact.title2}
          </h2>
          <p className="mt-6 text-[#fdfcf9]/75 leading-relaxed max-w-md">{t.contact.desc}</p>

          <div className="mt-10 space-y-3 text-[#fdfcf9]/85">
            <p data-testid="contact-info-address">
              <span className="block uppercase tracking-[0.2em] text-[10px] text-[#E2C9A0] mb-1">{t.contact.address}</span>
              Rambla del Celler, 93 · 08172 Sant Cugat del Vallès, Barcelona
            </p>
            <p data-testid="contact-info-phone">
              <span className="block uppercase tracking-[0.2em] text-[10px] text-[#E2C9A0] mb-1">{t.contact.phone}</span>
              {BRAND.phoneInfo}
            </p>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}
          className="bg-[#fdfcf9] text-[#2A2421] rounded-3xl p-8 md:p-10 space-y-5 shadow-[0_30px_60px_rgb(0,0,0,0.25)]"
          data-testid="contact-form"
        >
          <div>
            <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelName} *</Label>
            <Input id="c-name" data-testid="contact-input-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 bg-white border-[#eae0df]" required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="c-email" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelEmail} *</Label>
              <Input id="c-email" type="email" data-testid="contact-input-email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 bg-white border-[#eae0df]" required />
            </div>
            <div>
              <Label htmlFor="c-phone" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelPhone} *</Label>
              <Input id="c-phone" data-testid="contact-input-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="mt-1 bg-white border-[#eae0df]" required />
            </div>
          </div>
          <div>
            <Label htmlFor="c-message" className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">{t.contact.labelMessage}</Label>
            <Textarea id="c-message" data-testid="contact-input-message" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="mt-1 bg-white border-[#eae0df]" placeholder={t.contact.placeholderMessage} rows={4} />
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={submitting}
            className="w-full rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] py-6 text-sm uppercase tracking-[0.18em]">
            {submitting ? t.contact.submitting : t.contact.submit}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
